import { useState, useCallback } from 'react';

/**
 * Hook personalizado para consultar endereços via API ViaCEP
 * @returns {Object} Objeto contendo função de busca, estado de loading e dados do endereço
 */
const useViaCep = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Busca o endereço pelo CEP informado
   * @param {string} cep - CEP no formato com ou sem máscara
   * @returns {Promise<Object|null>} Dados do endereço ou null em caso de erro
   */
  const fetchAddress = useCallback(async (cep) => {
    if (!cep) {
      setError('CEP não informado');
      return null;
    }

    // Remove caracteres não numéricos
    const cleanCep = cep.replace(/\D/g, '');
    
    // Valida se tem 8 dígitos
    if (cleanCep.length !== 8) {
      setError('CEP deve ter 8 dígitos');
      return null;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`);
      
      if (!response.ok) {
        throw new Error('Erro na consulta do CEP');
      }

      const data = await response.json();

      // Verifica se o CEP foi encontrado
      if (data.erro) {
        throw new Error('CEP não encontrado');
      }

      setLoading(false);
      
      // Retorna os dados formatados para os campos do formulário
      return {
        street: data.logradouro || '',
        neighborhood: data.bairro || '',
        city: data.localidade || '',
        state: data.uf || '',
        complement: data.complemento || ''
      };

    } catch (err) {
      setLoading(false);
      setError(err.message);
      console.error('Erro ao buscar CEP:', err);
      return null;
    }
  }, []);

  return {
    fetchAddress,
    loading,
    error,
    clearError: () => setError(null)
  };
};

export default useViaCep;
